import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { FileText, Boxes, Users, Settings, UserCog, Plus, Loader2, Clock, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi } from "@/lib/api";

const MeContext = createContext(null);
export const useMe = () => useContext(MeContext);

const NAV = [
  { to: "/", label: "Preventivi", icon: FileText, id: "nav-quotes-link", end: true },
  { to: "/catalogo", label: "Catalogo & Kit", icon: Boxes, id: "nav-catalog-link" },
  { to: "/clienti", label: "Clienti", icon: Users, id: "nav-clients-link" },
  { to: "/utenti", label: "Utenti", icon: UserCog, id: "nav-users-link", admin: true },
  { to: "/confermati", label: "Report", icon: BadgeCheck, id: "nav-confirmed-link" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, id: "nav-settings-link", admin: true },
];

const Brand = () => (
  <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-link">
    <div className="h-8 w-8 rounded bg-amber-500 text-slate-950 grid place-items-center font-display font-bold">G</div>
    <div className="leading-tight">
      <div className="font-display font-semibold tracking-tight">GCAVCOM</div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Preventivi</div>
    </div>
  </Link>
);

const Header = ({ me }) => (
  <header className="sticky top-0 z-40 bg-[#0F1420]/95 backdrop-blur-md border-b border-slate-800">
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 h-14 flex items-center gap-6">
      <Brand />
      <nav className="hidden md:flex items-center gap-1">
        {NAV.filter((n) => !n.admin || me.role === "admin").map((n) => <NavItem key={n.to} n={n} />)}
      </nav>
      <div className="ml-auto flex items-center gap-3">
        <Button asChild size="sm" className="font-medium" data-testid="btn-create-quote">
          <Link to="/preventivi/nuovo"><Plus className="h-4 w-4 mr-1" />Nuovo preventivo</Link>
        </Button>
        <UserButton />
      </div>
    </div>
    <nav className="md:hidden flex gap-1 overflow-x-auto px-3 pb-2">
      {NAV.filter((n) => !n.admin || me.role === "admin").map((n) => <NavItem key={n.to} n={n} mobile />)}
    </nav>
  </header>
);

const NavItem = ({ n, mobile }) => (
  <NavLink
    to={n.to}
    end={n.end}
    data-testid={mobile ? `${n.id}-mobile` : n.id}
    className={({ isActive }) =>
      `flex items-center gap-2 px-3 py-1.5 rounded text-sm whitespace-nowrap transition-colors ${isActive ? "bg-slate-800 text-amber-400" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"}`}
  >
    <n.icon className="h-4 w-4" />{n.label}
  </NavLink>
);

const Pending = ({ me }) => (
  <div className="min-h-screen grid place-items-center p-6 grid-bg" data-testid="pending-screen">
    <div className="max-w-md text-center bg-slate-900 border border-slate-800 rounded-lg p-8">
      <Clock className="h-10 w-10 text-amber-400 mx-auto" />
      <h1 className="text-2xl font-semibold mt-4">Accesso in attesa di approvazione</h1>
      <p className="text-slate-400 mt-3 text-sm">Il tuo account ({me.email}) è stato registrato. Un amministratore deve abilitarti prima che tu possa vedere i preventivi.</p>
      <div className="mt-6 flex justify-center"><UserButton /></div>
    </div>
  </div>
);

export default function Layout() {
  const api = useApi();
  const [me, setMe] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get("/me").then((r) => setMe(r.data)).catch(() => setError(true));
  }, [api]);
  if (error) return <div className="p-10 text-center text-slate-400" data-testid="me-error">Impossibile caricare il profilo. Ricarica la pagina.</div>;
  if (!me) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>;
  if (me.role === "in_attesa") return <Pending me={me} />;
  return (
    <MeContext.Provider value={me}>
      <div className="min-h-screen">
        <Header me={me} />
        <main className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </MeContext.Provider>
  );
}
